import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { ServiceKey } from '@/lib/serviceKeys';

export interface OrgService {
  service_key: string;
  service_name: string;
  description: string | null;
  is_active: boolean; // active in registry
  is_enabled: boolean; // enabled for org's plan
}

/**
 * Phase 5 — Fetches all active services and resolves plan entitlement
 * for the current org. Fails closed if plan access cannot be verified.
 */
export function useOrgServices() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery<OrgService[]>({
    queryKey: ['org-services', orgId],
    queryFn: async () => {
      // 1. Fetch all active services from registry
      const { data: services, error: sErr } = await supabase
        .from('services')
        .select('id, service_key, service_name, description, is_active')
        .eq('is_active', true)
        .order('service_name');

      if (sErr || !services) return [];

      // 2. Check each service against the org plan
      const results: OrgService[] = await Promise.all(
        services.map(async (svc) => {
          const { data, error } = await supabase.rpc('check_org_service_access', {
            _org_id: orgId!,
            _service_key: svc.service_key,
          });
          const is_enabled = error ? false : data === false ? false : !!data;
          return {
            service_key: svc.service_key,
            service_name: svc.service_name,
            description: svc.description,
            is_active: svc.is_active,
            is_enabled,
          };
        })
      );

      return results;
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
}
